'use client';

import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { cn } from '@/utils/cn';

interface FilteredShowsFilterProps {
    availableFilters: AvailableFilter[];
    activeFilters: string[];
    filterAction: (filter: string) => void;
}

interface AvailableFilter {
    value: string;
    label: string;
}

export default function FilteredShowsFilter({
    availableFilters,
    activeFilters,
    filterAction,
}: FilteredShowsFilterProps): React.JSX.Element {
    const t = useTranslations('FilteredShows');
    const filterRef = useRef<HTMLDivElement>(null);
    const [announcement, setAnnouncement] = useState<string>('');

    // Function to announce filter changes to screen readers
    const announceFilterChange = (filterName: string, isActive: boolean) => {
        const message = isActive ? `${filterName} filter activated` : 'All filters cleared';
        setAnnouncement(message);

        // Clear the announcement after a delay
        setTimeout(() => {
            setAnnouncement('');
        }, 3000);
    };

    // Handle filter action with announcement
    const handleFilterAction = (value: string) => {
        const isActive = activeFilters.includes(value);
        filterAction(value);

        if (value === 'all') {
            announceFilterChange('All', !isActive);
        } else {
            const filter = availableFilters.find((f) => f.value === value);
            if (filter) {
                announceFilterChange(filter.label, !isActive);
            }
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent, value: string) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleFilterAction(value);
        }
    };

    return (
        <>
            {/* Screen reader announcement region */}
            <div className="sr-only" aria-live="polite" aria-atomic="true">
                {announcement}
            </div>

            <div
                ref={filterRef}
                className={cn(
                    'px-4',
                    'mt-0',
                    'flex min-h-8 py-2 lg:h-auto',
                    'font-neue-haas',
                    'flex-wrap gap-x-2 gap-y-2 sm:gap-y-4',
                    'items-center justify-st lg:justify-start'
                )}
            >
                <div className="mr-4 hidden lg:block" id="filter-label">
                    Filter:
                </div>
                <fieldset
                    aria-labelledby="filter-label"
                    className="flex flex-wrap gap-x-2 gap-y-2 sm:gap-y-4"
                >
                    <button
                        type="button"
                        onClick={() => handleFilterAction('all')}
                        onKeyDown={(e) => handleKeyDown(e, 'all')}
                        className={cn(
                            'rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-haug-coral-100',
                            activeFilters.length === 0 || activeFilters.includes('all')
                                ? 'bg-haug-coral-100 text-haug-ivory-100'
                                : 'border border-haug-choco-30 bg-haug-ivory-30'
                        )}
                        aria-pressed={activeFilters.length === 0 || activeFilters.includes('all')}
                    >
                        {t('show-all')}
                    </button>

                    {availableFilters.map((availableFilter) => (
                        <button
                            type="button"
                            key={availableFilter.value}
                            onClick={() => handleFilterAction(availableFilter.value)}
                            onKeyDown={(e) => handleKeyDown(e, availableFilter.value)}
                            className={cn(
                                'rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-haug-coral-100',
                                activeFilters.includes(availableFilter.value)
                                    ? 'bg-haug-coral-100 text-haug-ivory-100'
                                    : 'border border-haug-choco-30 bg-haug-ivory-30'
                            )}
                            aria-pressed={activeFilters.includes(availableFilter.value)}
                        >
                            {availableFilter.label}
                        </button>
                    ))}
                </fieldset>
            </div>
        </>
    );
}
